import React, { useContext, useEffect, useState } from 'react';
import { ConfigContext } from '../services/context/ConfigContext.js';
import { getConfig } from '../services/hooks/useApis.js';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../services/Constants/GlobalRoutes.jsx';
import MaintenancePage from '../pages/Maintenance/MaintenancePage.jsx';
import { UpdateMaintenanceMessageContext } from '../services/context/MaintenanceContext.js';
import { addBreadcrumb, captureException } from '@sentry/react';

/**
 * @param {object} props - The props for the controller
 * @param {React.ReactNode} props.slot - The content to display within this controller
 * @returns {JSX.Element} The ConfigController Component
 */
export default function ConfigController({ slot }) {
  const [config, setConfig] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [isMaintenance, setIsMaintenance] = useState(false);
  const setMaintenanceMessage = useContext(UpdateMaintenanceMessageContext);
  const navigateTo = useNavigate();

  const maxAttempts = 5;

  useEffect(() => {
    if (!config && attempts < maxAttempts && !isMaintenance) {
      getConfig()
        .then(fetchedConfig => {
          setConfig(fetchedConfig);
          setAttempts(0);

          const maintenanceFlag = fetchedConfig?.frontend?.general?.maintenanceWarning?.flows?.load;
          if (maintenanceFlag) {
            setMaintenanceMessage(fetchedConfig.frontend.general.maintenanceWarning.message);
            setIsMaintenance(true);
            navigateTo(ROUTES.MAINTENANCE, { replace: true });
          }
        })
        .catch(() => {
          setAttempts(prev => prev + 1);
        });
    }
  }, [
    config,
    attempts,
    isMaintenance,
    navigateTo,
    setMaintenanceMessage,
  ]);

  useEffect(() => {
    if (attempts >= maxAttempts && !config && !isMaintenance) {
      addBreadcrumb({
        category: 'config fetch attempts',
        message: `${attempts} attempts`,
      });
      captureException(new Error('Failed to fetch config'));
      setMaintenanceMessage('We have run into a technical problem. Please try again in a few seconds');
      setIsMaintenance(true);
      navigateTo(ROUTES.MAINTENANCE, { replace: true });
    }
  }, [
    attempts,
    config,
    isMaintenance,
    navigateTo,
    setMaintenanceMessage,
  ]);

  if (isMaintenance) {
    return <MaintenancePage />;
  }

  if (!config) {
    return null; // Show nothing while loading
  }

  return (
    <ConfigContext.Provider value={ config }>
      {slot}
    </ConfigContext.Provider>
  );
}

ConfigController.propTypes = { slot: PropTypes.element };
